/**
 * The Settings area.
 *
 * Settings used to be two entries in the profile dropdown — one opening a
 * modal, one a page. A dropdown is a menu, not a settings panel: it cannot hold
 * a heading, a description or a save state, and every option added to it made
 * the menu longer without making anything easier to find. So the dropdown keeps
 * one entry, Settings, and everything else lives here.
 *
 * Two columns: sections on the left, the chosen page on the right. Structure
 * comes from headings and spacing rather than from cards — a settings page
 * nested inside a card inside a panel is three borders around one list.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

const SECTIONS = [
  { to: '/settings/navigation', label: 'Navigation' },
  { to: '/settings/display', label: 'Display' },
];

interface SettingsLayoutProps {
  /** The section's own name — the breadcrumb tail and the page heading. */
  title: string;
  description: string;
  children: ReactNode;
  testId?: string;
}

const SettingsLayout = ({ title, description, children, testId }: SettingsLayoutProps) => (
  <div className="p-4 md:p-6" data-testid={testId}>
    {/* Context first: which area, which page, without going back to the menu. */}
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-1.5 text-[12px] text-ink-muted">
        <li>Settings</li>
        <li aria-hidden="true" className="text-gray-300 dark:text-gray-600">/</li>
        <li className="text-gray-600 dark:text-gray-300 font-medium">{title}</li>
      </ol>
    </nav>

    <div className="grid gap-6 md:gap-10 md:grid-cols-[176px_1fr]">
      {/* Section menu. Text only — two entries do not need icons to be told
          apart, and an icon beside each would be decoration standing in for
          information. On a narrow screen it becomes a row above the content. */}
      <nav aria-label="Settings sections" className="md:pt-1">
        <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
          Settings
        </p>
        <ul className="flex md:block gap-1 md:gap-0 overflow-x-auto md:overflow-visible">
          {SECTIONS.map((s) => (
            <li key={s.to}>
              <NavLink
                to={s.to}
                className={({ isActive }) =>
                  `block whitespace-nowrap rounded-md px-2 py-1.5 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    isActive
                      ? 'bg-indigo-50 dark:bg-indigo-400/10 text-indigo-700 dark:text-indigo-200 font-medium'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/40'
                  }`
                }
              >
                {s.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0">
        <header className="pb-4 mb-5 border-b border-gray-100 dark:border-gray-700/60">
          <h1 className="text-[17px] font-semibold text-ink tracking-tight">
            {title}
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>
        </header>
        {children}
      </div>
    </div>
  </div>
);

/** A labelled block within a settings page. Heading, hint, then the control. */
export const SettingsSection = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <section className="mb-8 last:mb-0">
    <h2 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
    {hint && <p className="mt-0.5 mb-3 text-[12.5px] text-ink-muted">{hint}</p>}
    <div className={hint ? '' : 'mt-3'}>{children}</div>
  </section>
);

export default SettingsLayout;
